import { create } from 'zustand';
import { supabase } from './supabase';
import { prefetchStudents } from './api';
import { withRetry } from './retry';
import { logger } from './logger';
import { persist } from 'zustand/middleware';

interface TableInfo {
  table_name: string;
  table_type: 'success' | 'error' | 'other';
}

interface Student {
  id: string;
  nome_completo: string;
  email: string;
  // ... outros campos
}

interface Course {
  id: string;
  codigo: string;
  nome: string;
  created_at: string;
}

interface AppState {
  tables: TableInfo[];
  students: Student[];
  courses: Course[];
  loading: boolean;
  error: string | null;
  deleteOnlyMode: boolean;
  setTables: (tables: TableInfo[]) => void;
  setStudents: (students: Student[]) => void;
  setCourses: (courses: Course[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setDeleteOnlyMode: (enabled: boolean) => void;
  fetchTables: () => Promise<void>;
  fetchStudents: () => Promise<void>;
  fetchCourses: () => Promise<void>;
  initializeData: () => Promise<void>;
}

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY_TABLES = 'tables_cache';
const CACHE_KEY_STUDENTS = 'students_cache';
const CACHE_KEY_COURSES = 'courses_cache';

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const isCacheValid = (timestamp: number) => {
  return Date.now() - timestamp < CACHE_DURATION;
};

const getCache = <T>(key: string): T | null => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached) as CacheItem<T>;
    if (!isCacheValid(timestamp)) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (error) {
    logger.error('Erro ao ler cache:', error as Error);
    return null;
  }
};

const setCache = <T>(key: string, data: T) => {
  try {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheItem));
  } catch (error) {
    logger.error('Erro ao salvar cache:', error as Error);
  }
};

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      tables: [],
      students: [],
      courses: [],
      loading: false,
      error: null,
      deleteOnlyMode: false,
      setTables: (tables) => set({ tables }),
      setStudents: (students) => set({ students }),
      setCourses: (courses) => set({ courses }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setDeleteOnlyMode: (enabled) => set({ deleteOnlyMode: enabled }),

      fetchTables: async () => {
        const { setTables, setLoading, setError } = get();
        
        // Check cache first
        const cachedTables = getCache<TableInfo[]>(CACHE_KEY_TABLES);
        if (cachedTables) {
          setTables(cachedTables);
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const { data, error } = await withRetry(() => 
            supabase
              .from('tabelas_view')
              .select('*')
              .order('table_name')
          );
          
          if (error) throw error;
          
          if (data) {
            setTables(data);
            setCache(CACHE_KEY_TABLES, data);
          }
        } catch (error) {
          logger.error('Erro ao buscar tabelas:', error);
          setError('Erro ao carregar as tabelas. Por favor, tente novamente.');
        } finally {
          setLoading(false);
        }
      },

      fetchStudents: async () => {
        const { setStudents, setLoading, setError } = get();
        
        // Check cache first
        const cachedStudents = getCache<Student[]>(CACHE_KEY_STUDENTS);
        if (cachedStudents) {
          setStudents(cachedStudents);
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const { data, error } = await withRetry(() =>
            supabase
              .from('students')
              .select('*')
              .order('nome_completo', { ascending: true })
          );
          
          if (error) throw error;
          
          if (data) {
            setStudents(data);
            setCache(CACHE_KEY_STUDENTS, data);
          }
        } catch (error) {
          logger.error('Erro ao buscar estudantes:', error);
          setError('Erro ao carregar os estudantes. Por favor, tente novamente.');
        } finally {
          setLoading(false);
        }
      },

      fetchCourses: async () => {
        const { setCourses, setLoading, setError } = get();
        
        setLoading(true);
        setError(null);

        try {
          logger.info('Buscando cursos...');
          const { data, error } = await withRetry(() =>
            supabase
              .from('cursos')
              .select('*')
              .order('nome')
          );
          
          if (error) {
            logger.error('Erro ao buscar cursos:', error);
            throw error;
          }
          
          if (data) {
            logger.info('Cursos encontrados:', data.length);
            setCourses(data);
            // Atualizar cache
            setCache(CACHE_KEY_COURSES, data);
          } else {
            logger.info('Nenhum curso encontrado');
          }
        } catch (error) {
          logger.error('Erro ao buscar cursos:', error);
          setError('Erro ao carregar os cursos. Por favor, tente novamente.');
        } finally {
          setLoading(false);
        }
      },

      initializeData: async () => {
        const { setLoading, setError } = get();
        setLoading(true);
        setError(null);

        try {
          // Iniciar prefetch de dados
          await prefetchStudents();
          
          // Buscar tabelas e cursos em paralelo
          await Promise.all([
            get().fetchTables(),
            get().fetchCourses()
          ]);
        } catch (error) {
          logger.error('Erro ao inicializar dados:', error);
          setError('Erro ao carregar dados iniciais. Por favor, tente novamente.');
        } finally {
          setLoading(false);
        }
      },
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        deleteOnlyMode: state.deleteOnlyMode
      })
    }
  )
);